/**
 * robots.txt and sitemap.xml, generated at build time.
 *
 * Generated rather than checked in for two reasons. The sitemap has to list
 * exactly the routes the router serves, and a hand-maintained copy drifts the
 * first time a page is added. And robots.txt has to differ per environment:
 * the staging site is a public URL on onrender.com, and without an explicit
 * ban Google indexes it and the band competes with itself in the search
 * results.
 */

// The extension is spelled out because vite.config.ts imports this module,
// and vite's native config loader resolves it without a bundler: without it
// the build warns that it will stop resolving extensionless paths. Type-only
// is not enough - the scan is syntactic. tsconfig.json allows the form via
// allowImportingTsExtensions, and nothing is emitted from here anyway.
import type { PageMeta } from "./pages.ts";

/** Escapes the five characters that may not appear as text in XML. */
function xmlText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** "https://example.ch/" -> "https://example.ch". No trailing slash anywhere. */
export function normaliseSiteUrl(siteUrl: string): string {
  return siteUrl.trim().replace(/\/+$/, "");
}

/** The absolute URL of a route path. "/" keeps its slash, others do not gain one. */
export function absoluteUrl(siteUrl: string, path: string): string {
  return `${normaliseSiteUrl(siteUrl)}${path === "/" ? "/" : path}`;
}

/**
 * No lastmod: it would be the build date on every entry after every deploy,
 * which tells a crawler that six unchanged pages changed. An omitted optional
 * element is better than a wrong one, and it keeps the output deterministic.
 */
export function renderSitemap(siteUrl: string, pages: PageMeta[]): string {
  const entries = pages
    .map(
      (page) =>
        "  <url>\n" +
        `    <loc>${xmlText(absoluteUrl(siteUrl, page.path))}</loc>\n` +
        `    <priority>${page.priority.toFixed(1)}</priority>\n` +
        "  </url>",
    )
    .join("\n");

  return (
    '<?xml version="1.0" encoding="UTF-8"?>\n' +
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n' +
    `${entries}\n` +
    "</urlset>\n"
  );
}

/**
 * `indexable` is false for staging. The disallow is not a security measure -
 * the staging site stays publicly reachable - it keeps a second copy of the
 * same content out of the search index.
 */
export function renderRobots(siteUrl: string, indexable: boolean): string {
  if (!indexable) {
    return (
      "# Staging. This copy of the website must not appear in search results.\n" +
      "User-agent: *\n" +
      "Disallow: /\n"
    );
  }

  return (
    "User-agent: *\n" +
    "Allow: /\n" +
    "\n" +
    `Sitemap: ${absoluteUrl(siteUrl, "/sitemap.xml")}\n`
  );
}
